import { Box, Divider, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SystemDropdown } from "../../components/UIToolkit/Inputs/SystemDropDown/SystemDropdown";
import type { DropdownItem } from "../../components/UIToolkit/Inputs/SystemDropDown/types";
import { FONT_SIZE, LETTER_SPACING } from "../../theme/tokens";
import { currentFyQuarter, currentFyQuarterToDateRange, fyQuarterRange, lastNFyQuarters } from "./fyQuarter";

const PRESET_COUNT = 6;
// CMS PBJ is a US-only feature; pin the picker chrome to US format regardless of
// the user's OS locale so admins see the same dates everywhere they work.
const US_DATE_FORMAT = "MM/dd/yyyy";
// Sentinel chip value for the "current quarter to date" preset; every other
// preset value is the `year-quarter` of a completed FY quarter.
const CURRENT_PRESET_VALUE = "current";

type Range = { start: DateTime; end: DateTime };

type Props = {
	start: DateTime;
	end: DateTime;
	onChange: (next: Range) => void;
	disabled?: boolean;
};

// True when (start, end) exactly matches an FY-quarter window — used to highlight
// the preset chip that corresponds to the current range so users immediately see
// "I'm on Q2 FY2026" without having to mentally diff the dates.
const rangeMatchesQuarter = (start: DateTime, end: DateTime, year: number, quarter: number): boolean => {
	const range = fyQuarterRange(year, quarter);
	return range.start.toISODate() === start.toISODate() && range.end.toISODate() === end.toISODate();
};

const rangeMatchesCurrentQuarterToDate = (start: DateTime, end: DateTime, today: DateTime): boolean => {
	const range = currentFyQuarterToDateRange(today);
	return range.start.toISODate() === start.toISODate() && range.end.toISODate() === end.toISODate();
};

export const PeriodSelector: FC<Props> = ({ start, end, onChange, disabled = false }) => {
	const { t } = useTranslation();

	const today = useMemo(() => DateTime.now(), []);
	const presets = useMemo(() => lastNFyQuarters(today, PRESET_COUNT), [today]);
	const currentQuarter = useMemo(() => currentFyQuarter(today), [today]);
	const currentQuarterActive = rangeMatchesCurrentQuarterToDate(start, end, today);

	const handlePresetClick = (year: number, quarter: number) => {
		onChange(fyQuarterRange(year, quarter));
	};

	const handleCurrentQuarterClick = () => {
		onChange(currentFyQuarterToDateRange(today));
	};

	// The DatePicker passes `null` on clear; ignore those — the page state always
	// holds a valid range, and clearing isn't a meaningful operation here.
	const handleStartChange = (next: DateTime | null) => {
		if (next?.isValid) onChange({ start: next.startOf("day"), end });
	};
	const handleEndChange = (next: DateTime | null) => {
		if (next?.isValid) onChange({ start, end: next.startOf("day") });
	};

	const dayCount = useMemo(() => Math.round(end.diff(start, "days").days) + 1, [start, end]);

	// Presets offered as a compact single-select dropdown (keeps the top bar to one row).
	const presetItems: DropdownItem<string>[] = useMemo(
		() => [
			{
				value: CURRENT_PRESET_VALUE,
				label: t("pbj_model.period.current_quarter_preset", { quarter: currentQuarter.quarter, year: currentQuarter.year }),
				disabled,
			},
			...presets.map(({ year, quarter }) => ({ value: `${year}-${quarter}`, label: t("pbj_model.period.quarter_preset", { quarter, year }), disabled })),
		],
		[t, currentQuarter, presets, disabled],
	);

	// The active FY-quarter preset (or the current-quarter sentinel) matching the live
	// range; null when the range is a custom hand-picked window, leaving the dropdown
	// showing only its "Quick pick" label.
	const activePresetValue = useMemo(() => {
		if (currentQuarterActive) return CURRENT_PRESET_VALUE;
		const match = presets.find(({ year, quarter }) => rangeMatchesQuarter(start, end, year, quarter));
		return match ? `${match.year}-${match.quarter}` : null;
	}, [currentQuarterActive, presets, start, end]);

	const handlePresetSelect = (value?: string) => {
		if (!value) return;
		if (value === CURRENT_PRESET_VALUE) {
			handleCurrentQuarterClick();
			return;
		}
		const [year, quarter] = value.split("-").map(Number);
		handlePresetClick(year, quarter);
	};

	return (
		<LocalizationProvider dateAdapter={AdapterLuxon}>
			<Box data-testid="pbj-model-period-selector" sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
				{/* Primary path: a completed FY quarter (or current-to-date) preset. */}
				<SystemDropdown<string>
					data-testid="pbj-model-presets"
					label={t("pbj_model.period.quick_pick")}
					items={presetItems}
					selectedItem={activePresetValue ?? undefined}
					onChange={handlePresetSelect}
					disabled={disabled}
					menuWidth={260}
					fullWidth
				/>

				{/* Secondary path: a hand-picked window. The divider labels it as the alternative to the presets above. */}
				<Divider sx={{ "& .MuiDivider-wrapper": { px: 1.5 } }}>
					<Typography
						component="span"
						sx={{
							fontSize: FONT_SIZE.LABEL_XS,
							fontWeight: 600,
							letterSpacing: LETTER_SPACING.LABEL,
							textTransform: "uppercase",
							color: "text.secondary",
						}}
					>
						{t("pbj_model.period.or_custom_range")}
					</Typography>
				</Divider>

				{/* Start – end on one aligned row: the dash sits in its own centered grid column so it
				    lines up with the middle of the input boxes rather than floating above them. */}
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) auto minmax(0, 1fr)" },
						alignItems: "center",
						columnGap: 1.25,
						rowGap: 1,
					}}
				>
					<Box data-testid="pbj-model-start-date" sx={{ minWidth: 0 }}>
						<DatePicker
							value={start}
							onChange={handleStartChange}
							label={t("pbj_model.period.start_date")}
							maxDate={end}
							disabled={disabled}
							format={US_DATE_FORMAT}
							slotProps={{ textField: { size: "small", fullWidth: true } }}
						/>
					</Box>
					<Typography variant="body2" color="text.secondary" aria-hidden sx={{ display: { xs: "none", sm: "block" }, lineHeight: 1 }}>
						–
					</Typography>
					<Box data-testid="pbj-model-end-date" sx={{ minWidth: 0 }}>
						<DatePicker
							value={end}
							onChange={handleEndChange}
							label={t("pbj_model.period.end_date")}
							minDate={start}
							disabled={disabled}
							format={US_DATE_FORMAT}
							slotProps={{ textField: { size: "small", fullWidth: true } }}
						/>
					</Box>
				</Box>

				{/* Calm caption tied to the range above, not a control competing with the inputs. */}
				<Typography variant="caption" color="text.secondary" data-testid="pbj-model-period-range" sx={{ textAlign: "center" }}>
					{t("pbj_model.period.day_count", { count: dayCount })}
				</Typography>
			</Box>
		</LocalizationProvider>
	);
};
